import React from 'react';

/**
 * ErrorBoundary - global crash safety net.
 *
 * Catches uncaught render-time JavaScript errors in the React tree below
 * and renders a friendly fallback instead of the white-screen-of-death.
 *
 * Usage:
 *   <ErrorBoundary scope="portal">
 *     <PortalDashboard />
 *   </ErrorBoundary>
 *
 * Props:
 *   - scope  Free-form tag included in the console + analytics breadcrumb so
 *            you can tell which boundary caught the crash.
 *   - reset  Optional reset hook. The "Try again" button calls
 *            `setState({ hasError: false })` AND `props.reset?.()`.
 *
 * The fallback uses the canonical Mundalo cream aesthetic (no white-on-white,
 * no FRG colors). It also offers a "Reload" button - a hard refresh resolves
 * most transient `Cannot read property 'x' of undefined` cases caused by
 * stale data after a deploy.
 *
 * Implementation note: written as a class because React's hooks API has no
 * `useErrorBoundary` equivalent. The class API is the only way to catch
 * descendant render errors.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    const scope = this.props.scope || 'root';
    // Loud console error so the crash is grepable in the user's browser.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${scope}] caught a render error`, error, info);

    // Best-effort beacon to backend so we can debug crashes happening on
    // real user sessions. Fire-and-forget; we never want this to throw.
    try {
      const API = process.env.REACT_APP_BACKEND_URL || '';
      const body = JSON.stringify({
        scope,
        message: String(error?.message || error || '').slice(0, 500),
        stack: String(error?.stack || '').slice(0, 2000),
        component_stack: String(info?.componentStack || '').slice(0, 2000),
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        at: new Date().toISOString(),
      });
      // sendBeacon survives page unloads and never blocks render.
      if (navigator && navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API}/api/client-errors`, blob);
      } else {
        fetch(`${API}/api/client-errors`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => { /* swallow */ });
      }
    } catch (_) {
      // Don't let the reporter itself break the boundary.
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
    this.props.reset?.();
  };

  handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback } = this.props;
    if (fallback) return fallback;

    return (
      <div
        role="alert"
        className="min-h-[60vh] flex items-center justify-center bg-[#F9F6EF] px-6 py-16"
        data-testid="error-boundary-fallback"
      >
        <div className="max-w-md w-full bg-white border border-[#EFE6CF] rounded-2xl p-8 sm:p-10 text-center shadow-[0_1px_3px_rgba(10,31,46,0.05)]">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-3">
            Something went wrong
          </p>
          <h2 className="font-display italic text-3xl text-[#0A1F2E] leading-tight mb-3">
            This page hit a snag.
          </h2>
          <p className="text-[#0A1F2E]/70 font-body text-sm mb-7">
            Our team has been notified. Try again in a moment, or reload to
            refresh the page. If it keeps happening, write to{' '}
            <a
              href="mailto:hello@mundalo.com"
              className="text-[#1A5C6E] underline decoration-[#E8A830] underline-offset-2"
            >
              hello@mundalo.com
            </a>
            .
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center gap-2 bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold px-5 py-3 rounded-full transition"
              data-testid="error-boundary-retry"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#0A1F2E]/20 hover:border-[#0A1F2E]/45 text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.16em] font-semibold px-5 py-3 rounded-full transition"
              data-testid="error-boundary-reload"
            >
              Reload page
            </button>
          </div>

          {process.env.NODE_ENV !== 'production' && this.state.error?.message ? (
            <pre
              className="mt-6 text-left text-[11px] text-[#0A1F2E]/60 bg-[#F4ECD8] rounded-lg p-3 overflow-x-auto"
              data-testid="error-boundary-detail"
            >
              {String(this.state.error.message)}
            </pre>
          ) : null}
        </div>
      </div>
    );
  }
}
